'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import type { CartData, CartItem } from './api-types';

// ─── Constants ──────────────────────────────────────────────

export const FREE_SHIPPING_THRESHOLD = 80; // EUR

// ─── Types ──────────────────────────────────────────────────

interface CartContextValue {
  /** Current cart data (items, subtotal, count). */
  cart: CartData;
  /** True while the initial fetch is in-flight. */
  loading: boolean;
  /** Last fetch error message, or null. */
  error: string | null;
  /** True while any mutation (add/remove/quantity) is in-flight. */
  mutating: boolean;
  /** Re-fetch cart from PHP API. */
  refreshCart: () => Promise<void>;
  /** Replace cart data (e.g. after optimistic update). */
  setCart: (data: CartData) => void;
  /** Amount remaining to qualify for free shipping (0 if already qualified). */
  amountToFreeShipping: number;
  /** Percentage progress toward free shipping threshold (0-100). */
  shippingProgress: number;
  /** Add a product to the cart. */
  addToCart: (params: AddToCartParams) => Promise<void>;
  /** Increase item quantity by 1. */
  increaseQuantity: (extrasId: number) => Promise<void>;
  /** Decrease item quantity by 1 (removes if quantity becomes 0). */
  decreaseQuantity: (extrasId: number) => Promise<void>;
  /** Remove an item from the cart entirely. */
  removeItem: (extrasId: number) => Promise<void>;
}

export interface AddToCartParams {
  productId?: number;
  extrasId?: number;
  attributeId?: number[];
  quantity?: number;
}

// ─── Defaults ───────────────────────────────────────────────

const emptyCart: CartData = {
  items: [],
  itemCount: 0,
  subtotal: 0,
  currency: 'EUR',
};

const CartContext = createContext<CartContextValue>({
  cart: emptyCart,
  loading: true,
  error: null,
  mutating: false,
  refreshCart: async () => {},
  setCart: () => {},
  amountToFreeShipping: FREE_SHIPPING_THRESHOLD,
  shippingProgress: 0,
  addToCart: async () => {},
  increaseQuantity: async () => {},
  decreaseQuantity: async () => {},
  removeItem: async () => {},
});

// ─── Fetch helper (client-side) ─────────────────────────────

async function fetchCartClient(): Promise<CartData> {
  const res = await fetch('/api/store/cart', {
    credentials: 'include',
    cache: 'no-store',
    signal: AbortSignal.timeout(5000),
  });
  if (!res.ok) {
    throw new Error(`Cart fetch failed: ${res.status}`);
  }
  const json = await res.json();
  return json.data ?? json;
}

// ─── Mutation helper ────────────────────────────────────────

async function mutateCart(url: string, body?: Record<string, unknown>, method = 'POST'): Promise<CartData> {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(10000),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const errData = errBody?.error ?? errBody?.data?.error ?? errBody;
    const message = typeof errData === 'string' ? errData : errData?.message;
    throw new Error(message ?? `Cart mutation failed: ${res.status}`);
  }
  const json = await res.json();
  return json.data ?? json;
}

// ─── Provider ───────────────────────────────────────────────

export function CartProvider({
  initial,
  children,
}: {
  initial?: CartData | null;
  children: React.ReactNode;
}) {
  const [cart, setCartState] = useState<CartData>(initial ?? emptyCart);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  const refreshCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCartClient();
      setCartState(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, []);

  const setCart = useCallback((data: CartData) => {
    setCartState(data);
  }, []);

  // Fetch cart on mount if no initial data provided
  useEffect(() => {
    if (!initial) {
      refreshCart();
    }
  }, [initial, refreshCart]);

  // ─── Mutation methods ───────────────────────────────────

  const addToCart = useCallback(async (params: AddToCartParams) => {
    setMutating(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (params.extrasId) body.pe_id = params.extrasId;
      if (params.productId) body.product_id = params.productId;
      if (params.attributeId) body.attribute_id = params.attributeId;
      if (params.quantity) body.quantity = params.quantity;
      const data = await mutateCart('/api/store/cart/add', body);
      setCartState(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item');
      throw err;
    } finally {
      setMutating(false);
    }
  }, []);

  const increaseQuantity = useCallback(async (extrasId: number) => {
    setMutating(true);
    setError(null);
    try {
      const data = await mutateCart('/api/store/cart/increase', { pe_id: extrasId });
      setCartState(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update quantity');
      throw err;
    } finally {
      setMutating(false);
    }
  }, []);

  const decreaseQuantity = useCallback(async (extrasId: number) => {
    setMutating(true);
    setError(null);
    try {
      const data = await mutateCart('/api/store/cart/decrease', { pe_id: extrasId });
      setCartState(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update quantity');
      throw err;
    } finally {
      setMutating(false);
    }
  }, []);

  const removeItem = useCallback(async (extrasId: number) => {
    setMutating(true);
    setError(null);
    try {
      const data = await mutateCart(`/api/store/cart/${extrasId}`, undefined, 'DELETE');
      setCartState(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item');
      throw err;
    } finally {
      setMutating(false);
    }
  }, []);

  const amountToFreeShipping = useMemo(
    () => Math.max(0, FREE_SHIPPING_THRESHOLD - cart.subtotal),
    [cart.subtotal]
  );

  const shippingProgress = useMemo(
    () => Math.min(100, (cart.subtotal / FREE_SHIPPING_THRESHOLD) * 100),
    [cart.subtotal]
  );

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      loading,
      error,
      mutating,
      refreshCart,
      setCart,
      amountToFreeShipping,
      shippingProgress,
      addToCart,
      increaseQuantity,
      decreaseQuantity,
      removeItem,
    }),
    [cart, loading, error, mutating, refreshCart, setCart, amountToFreeShipping, shippingProgress, addToCart, increaseQuantity, decreaseQuantity, removeItem]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────

export function useCart(): CartContextValue {
  return useContext(CartContext);
}
